import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { api, formatApiError } from "@/api";
import { toast } from "sonner";
import { ArrowLeft, ShieldCheck, CheckCircle2, Loader2 } from "lucide-react";
import { StripeCheckoutCard } from "@/components/checkout/StripeCheckoutCard";

/**
 * Page publique de checkout — PayPal et/ou Stripe (carte bancaire), selon l'activation côté admin (Phase 74).
 * Route : /pay/checkout/:inscriptionId
 */
export default function PayCheckout() {
  const { inscriptionId } = useParams();
  const navigate = useNavigate();
  const [info, setInfo] = useState(null);
  const [paypalCfg, setPaypalCfg] = useState(null);
  const [stripeCfg, setStripeCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [captured, setCaptured] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: i }, { data: p }, st] = await Promise.all([
          api.get(`/public/paypal/inscription/${inscriptionId}`),
          api.get(`/public/paypal/config`),
          api.get(`/public/stripe/config`).catch(() => ({ data: { enabled: false } })),
        ]);
        setInfo(i);
        setPaypalCfg(p);
        setStripeCfg(st.data);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Impossible de charger cette inscription");
      } finally { setLoading(false); }
    })();
  }, [inscriptionId]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <Loader2 className="h-8 w-8 animate-spin text-[#002FA7]" />
    </div>
  );

  if (!info) return (
    <div className="min-h-screen flex items-center justify-center bg-white p-6">
      <div className="max-w-md text-center">
        <div className="font-display text-3xl font-black tracking-tighter mb-3">Lien invalide</div>
        <p className="text-[#495057] mb-6">Cette inscription n'existe pas ou n'a pas encore été validée.</p>
        <Link to="/" className="underline font-semibold text-[#002FA7]">Retour à l'accueil</Link>
      </div>
    </div>
  );

  if (captured) return (
    <div className="min-h-screen flex items-center justify-center bg-[#059669] p-6">
      <div className="max-w-lg bg-white p-10 border-2 border-[#0A0A0A]">
        <CheckCircle2 className="h-14 w-14 text-[#059669] mb-4" />
        <h1 className="font-display text-4xl font-black tracking-tighter mb-3">Paiement confirmé.</h1>
        <p className="text-[#495057] mb-4">
          Votre paiement PayPal de <b>{captured.amount_captured?.toFixed(2)} EUR</b> a été enregistré.
          Vos identifiants vous seront envoyés par email sous quelques instants.
        </p>
        {captured.activation?.entreprise_code && (
          <div className="p-4 bg-[#F8F9FA] border border-[#DEE2E6] font-mono text-sm space-y-1" data-testid="pay-success-creds">
            <div><span className="text-[#868E96]">Code entreprise :</span> <b>{captured.activation.entreprise_code}</b></div>
            {captured.activation.chef_password && (
              <div><span className="text-[#868E96]">Mot de passe :</span> <b>{captured.activation.chef_password}</b></div>
            )}
          </div>
        )}
        <button onClick={() => navigate("/")} data-testid="pay-success-home" className="mt-6 h-11 px-6 bg-[#0A0A0A] text-white font-semibold">
          Retour à l'accueil
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <header className="border-b border-[#DEE2E6] bg-white">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src="/pag-logo.png" alt="PAG" className="h-10 w-10" />
            <span className="font-display font-black text-xl">PAG</span>
          </Link>
          <Link to="/" className="text-sm font-semibold flex items-center gap-2 hover:text-[#002FA7]">
            <ArrowLeft className="h-4 w-4" /> Accueil
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto p-6 lg:p-12" data-testid="pay-checkout">
        <div className="text-xs uppercase tracking-[0.3em] font-semibold text-[#0070BA] mb-3">// Paiement sécurisé</div>
        <h1 className="font-display text-4xl lg:text-5xl font-black tracking-tighter mb-2">Finalisez votre abonnement.</h1>
        <p className="text-[#495057] mb-8" data-testid="pay-intro">{stripeCfg?.enabled ? "Choisissez votre moyen de paiement : PayPal ou carte bancaire via Stripe. Abonnement récurrent, résiliable à tout moment." : "Paiement 100% sécurisé via PayPal — pas besoin de compte, la carte bancaire est acceptée en invité."}</p>

        {/* Récapitulatif */}
        <div className="bg-white border border-[#DEE2E6] p-6 lg:p-8 mb-6" data-testid="pay-recap">
          <div className="text-xs uppercase tracking-[0.25em] font-semibold text-[#868E96] mb-4">Récapitulatif</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">Entreprise</div>
              <div className="mt-1 font-semibold">{info.raison_sociale}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">Formule</div>
              <div className="mt-1 font-semibold">{info.plan_name}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">Cycle</div>
              <div className="mt-1 font-semibold uppercase">{info.cycle}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#868E96] font-semibold">À payer</div>
              <div className="mt-1 font-display font-black text-2xl text-[#0070BA]" data-testid="pay-amount">{info.amount.toFixed(2)} €</div>
            </div>
          </div>
          {info.already_paid && (
            <div className="mt-4 p-3 bg-[#ECFDF5] border border-[#A7F3D0] text-sm text-[#065F46]" data-testid="pay-already-paid">
              ✓ Ce paiement a déjà été enregistré. Consultez vos emails pour vos identifiants.
            </div>
          )}
        </div>

        <div className={stripeCfg?.enabled ? "grid lg:grid-cols-2 gap-6" : ""} data-testid="pay-methods" data-stripe={stripeCfg?.enabled ? "1" : "0"}>
        {/* PayPal */}
        <div className="bg-white border border-[#DEE2E6] p-6 lg:p-8" data-testid="pay-method-paypal">
          <div className="flex items-center gap-3 mb-4">
            <div className="h-10 w-10 flex items-center justify-center bg-[#0070BA] rounded font-bold text-white text-sm">PP</div>
            <div>
              <div className="font-display font-bold text-xl">PayPal</div>
              <div className="text-xs text-[#868E96]">Abonnement récurrent · Renouvellement automatique</div>
            </div>
          </div>
          <p className="text-sm text-[#495057] mb-6">
            Souscrivez avec votre compte PayPal ou une carte bancaire en invité (Visa, Mastercard, Amex).
            L'abonnement se renouvelle automatiquement à la fin de chaque cycle. Résiliable à tout moment.
          </p>
          {paypalCfg?.enabled ? (
            <PayPalScriptProvider
              options={{
                "client-id": paypalCfg.client_id,
                currency: "EUR",
                intent: "subscription",
                vault: true,
                components: "buttons",
                locale: "fr_FR",
              }}
            >
              <PayPalButtons
                disabled={processing || info.already_paid}
                style={{ layout: "vertical", color: "gold", shape: "rect", label: "subscribe" }}
                createSubscription={async () => {
                  try {
                    const { data } = await api.post("/public/paypal/subscriptions", { inscription_id: inscriptionId });
                    return data.subscription_id;
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Erreur PayPal");
                    throw e;
                  }
                }}
                onApprove={async (data) => {
                  setProcessing(true);
                  try {
                    const { data: r } = await api.post(`/public/paypal/subscriptions/${data.subscriptionID}/activate`);
                    if (r.status === "ACTIVE") {
                      setCaptured({
                        amount_captured: info.amount,
                        activation: r.activation,
                        subscription_id: r.subscription_id,
                        next_billing_time: r.next_billing_time,
                      });
                      toast.success("Abonnement PayPal confirmé");
                    } else {
                      toast.info(`Statut PayPal : ${r.status} — nous vous confirmerons par email`);
                      setCaptured({ amount_captured: info.amount, activation: r.activation, pending: true });
                    }
                  } catch (e) {
                    toast.error(formatApiError(e.response?.data?.detail) || "Erreur activation PayPal");
                  } finally { setProcessing(false); }
                }}
                onError={(err) => {
                  console.error("PayPal error", err);
                  toast.error("Erreur PayPal — veuillez réessayer");
                }}
              />
            </PayPalScriptProvider>
          ) : (
            <div className="text-xs text-[#868E96] p-3 border border-dashed border-[#DEE2E6]" data-testid="paypal-not-configured">
              PayPal est temporairement indisponible. Contactez l'équipe PAG : contact@parcavenue.eu · 09 70 70 17 92
            </div>
          )}
        </div>
        {stripeCfg?.enabled && <StripeCheckoutCard inscriptionId={inscriptionId} disabled={processing || info.already_paid} mode={stripeCfg.mode} />}
        </div>

        <div className="mt-8 flex items-center gap-2 text-xs text-[#868E96]">
          <ShieldCheck className="h-3.5 w-3.5" />
          Vos données de paiement sont chiffrées et traitées directement par {stripeCfg?.enabled ? "PayPal ou Stripe" : "PayPal"} — PAG ne stocke aucune information bancaire.
        </div>
      </main>
    </div>
  );
}
